import { installBrowserNexusFederation, type NexusEvent } from '@inyron/living-universe'
import { fighters, arenas } from './content'
import { lifeTelemetry, type LifeTelemetry } from './lifeOmega'
import { fightLivingUniverse } from './livingUniverse'
import { useFightStore } from './store'

type FighterWire={
 id:string;health:number;maxHealth:number;stamina:number;superMeter:number;
 hitstun:number;blocking:boolean;parryWindow:number;attackPhase:string|null;x:number;z:number
}
type WorkerStatus={
 ready:boolean;mode:'worker';entities:number;cycles:number;ticks:number;
 lastTaskMs:number;maxTaskMs:number;lastHeartbeatAt:number;savedAt:number;fault:string|null
}
const storageKey='inyron:living-universe:'+fightLivingUniverse.gameId+':'+fightLivingUniverse.worldId
function projectFighter(index:number,state:ReturnType<typeof useFightStore.getState>['player']):FighterWire{
 return{id:fighters[index]?.id??'unknown',health:state.health,maxHealth:state.maxHealth,
  stamina:state.stamina,superMeter:state.superMeter,hitstun:state.hitstun,
  blocking:state.blocking,parryWindow:state.parryWindow,attackPhase:state.attackPhase,
  x:state.x,z:state.z}
}
export function startFightLivingWorkerBridge():()=>void{
 const worker=new Worker(new URL('./FightLivingWorker.ts',import.meta.url),{type:'module',name:'inyron-fight-living-universe'})
 let alive=true,flushScheduled=false,latestSnapshotRaw:string|null=null,lastSent=0,lastIndex=-1,lastEnemy=-1,lastArena=-1
 let lastImpact=-1,lastMatch=-1,lastStatus=''
 const status:WorkerStatus={ready:false,mode:'worker',entities:0,cycles:0,ticks:0,lastTaskMs:0,
  maxTaskMs:0,lastHeartbeatAt:0,savedAt:0,fault:null}
 const diagnostics=window as typeof window&{__INYRON_LIVING_WORKER__?:WorkerStatus}
 diagnostics.__INYRON_LIVING_WORKER__=status
 const persist=()=>{
  flushScheduled=false
  if(!latestSnapshotRaw)return
  try{window.localStorage.setItem(storageKey,latestSnapshotRaw);status.savedAt=Date.now();latestSnapshotRaw=null}
  catch(error){status.fault='persistence:'+String(error)}
 }
 const queueSave=(snapshot:unknown)=>{
  latestSnapshotRaw=JSON.stringify(snapshot)
  if(flushScheduled)return
  flushScheduled=true
  if(typeof window.requestIdleCallback==='function')
   window.requestIdleCallback(persist,{timeout:2400})
  else globalThis.setTimeout(persist,0)
 }
 const fwd= fightLivingUniverse.nexus.subscribe(event=>{
  if(alive&&event.worldId!==fightLivingUniverse.worldId)worker.postMessage({kind:'external',event})
 })
 const federation=installBrowserNexusFederation(fightLivingUniverse)
 worker.onmessage=(message:MessageEvent)=>{
  if(!alive)return
  const p=message.data as {kind:string;entities?:number;cycles?:number;ticks?:number;
   lastTaskMs?:number;maxTaskMs?:number;event?:NexusEvent;snapshot?:unknown;
   message?:string;stage?:string}
  if(p.kind==='ready'){status.ready=true;status.entities=p.entities??0;status.lastHeartbeatAt=performance.now()}
  else if(p.kind==='heartbeat'){
   status.cycles=p.cycles??0;status.ticks=p.ticks??0;status.lastTaskMs=p.lastTaskMs??0
   status.maxTaskMs=p.maxTaskMs??0;status.entities=p.entities??status.entities
   status.lastHeartbeatAt=performance.now()
  }else if(p.kind==='snapshot'&&p.snapshot)queueSave(p.snapshot)
  else if(p.kind==='nexus'&&p.event)fightLivingUniverse.nexus.ingest(p.event)
  else if(p.kind==='fault'){status.fault=(p.stage??'worker')+':'+p.message;console.warn('[Fight Living Worker]',status.fault)}
 }
 worker.onerror=event=>{status.fault='worker-failure:'+event.message;console.error('[Fight Living Worker]',event.message)}
 let saved:string|null=null
 try{saved=window.localStorage.getItem(storageKey)}catch{}
 worker.postMessage({kind:'init',snapshotRaw:saved})
 const send=(s:ReturnType<typeof useFightStore.getState>,prev?:ReturnType<typeof useFightStore.getState>)=>{
  if(!alive)return
  const now=performance.now()
  const selection=s.playerIndex!==lastIndex||s.enemyIndex!==lastEnemy||s.arenaIndex!==lastArena
  const impact=s.impactCue&&s.impactSerial!==lastImpact
  const ending=s.status==='match-over'&&s.status!==lastStatus
  if(!selection&&!impact&&!ending&&now-lastSent<380)return
  lastSent=now;lastIndex=s.playerIndex;lastEnemy=s.enemyIndex;lastArena=s.arenaIndex
  lastStatus=s.status
  const packet:{
   kind:'state';player:FighterWire;enemy:FighterWire;arenaId:string;
   impact?:{serial:number;kind:string;attackKind:string|null;source:'player'|'enemy';playerTelemetry:LifeTelemetry;enemyTelemetry:LifeTelemetry};
   match?:{serial:number;status:string;winner:'player'|'enemy'|'draw'|null}
  }={
   kind:'state',player:projectFighter(s.playerIndex,s.player),enemy:projectFighter(s.enemyIndex,s.enemy),
   arenaId:arenas[s.arenaIndex]?.id??'aurelios',
   match:{serial:s.matchSerial,status:s.status,winner:s.winner}
  }
  if(impact&&s.impactCue){
   lastImpact=s.impactSerial
   packet.impact={serial:s.impactSerial,kind:s.impactCue.kind,
    attackKind:s.impactCue.attackKind,source:s.impactCue.source,
    playerTelemetry:lifeTelemetry(s.playerLife,'T0'),enemyTelemetry:lifeTelemetry(s.enemyLife,'T0')}
  }
  worker.postMessage(packet)
  void prev
 }
 const unsubscribe=useFightStore.subscribe(send)
 send(useFightStore.getState())
 const pagehide=()=>{persist();worker.postMessage({kind:'save'})}
 window.addEventListener('pagehide',pagehide)
 return()=>{
  alive=false
  unsubscribe();federation.stop();fwd()
  window.removeEventListener('pagehide',pagehide)
  persist();worker.postMessage({kind:'stop'})
  window.setTimeout(()=>worker.terminate(),250)
 }
}
